import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../../components/common/Toast/toast-type'
import { ErrorBoundary } from '../../../components/common/ErrorBoundary'
import { theme } from '../../../theme/theme'
import { Icon, IconChoices } from '../../../components/common/Icon'
import { Button, ButtonVariant } from '../../../components/common/Button'
import { TextInput } from '../../../components/common/TextInput'
import { FormControl } from '../../../components/common/FormControl'
import { reverseUrl } from '../../../navigation/reverseUrl'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useAuth } from '../../../hooks/useAuth'
import { useFormErrorMessage } from '../../../hooks/useFormErrorMessage'
import { useLinkToken } from '../../../hooks/useLinkToken'
import { useLinkLiveness } from '../../../hooks/useLinkLiveness'
import { isRateLimited } from '../../../helpers/rate_limit'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import {
  Illustration,
  IllustrationChoices,
} from '../../../components/common/Illustration'
import { createUser, resetPassword } from '../../../api/users'
import { resetPasswordSchema } from '../../../forms/schemas/reset_password'
import { getLanguage } from '../../../helpers/i18n'
import i18n from '../../../i18n/i18n'
import { PasswordForm } from '../../../components/authentication/PasswordForm'
import {
  setSecureBearerCookies,
  setSecureUserIdCookie,
} from '../../../helpers/authentication'

interface VerifyEmailLayoutProps {
  children: React.ReactNode
}

const VerifyEmailLayout = ({
  children,
}: VerifyEmailLayoutProps): React.ReactNode => {
  return (
    <ErrorBoundary>
      <div className="flex h-full w-full flex-row">
        <div className="mx-6 h-full flex-1">
          <Icon
            className="mt-8"
            icon={IconChoices.BOAPPA}
            width={123}
            height={45}
          />
          <div className="mt-8 flex flex-col md:ml-32 md:mt-52">{children}</div>
        </div>
        <div
          className="flex hidden h-full w-full flex-1 items-center
         justify-center bg-brandLightGreen lg:flex"
        >
          <Illustration
            illustrationChoice={IllustrationChoices.SUN}
            width={400}
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}

// The end of the link in the mail. Setting the password is what proves the
// address, so there is nothing to confirm first and nothing to poll for: the
// call that saves the password answers with a session.
const SetPassword = ({
  token,
  onLinkDead,
}: {
  token: string
  onLinkDead: () => void
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { setUserId } = useAuth()
  const navigate = useNavigate()
  const client = useQueryClient()

  const { mutate: mutateSetPassword, isPending } = useMutation({
    mutationFn: async (password: string) => {
      const response = await resetPassword(token, password)
      return response.data.data
    },
    onSuccess: (data) => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('verifyEmailView.setPassword.flashMessage.success')
      )
      setSecureBearerCookies(data.token)
      setSecureUserIdCookie(data.user._id)
      setUserId(data.user._id)
      navigate(reverseUrl('feed'))
      client.resetQueries()
    },
    onError: (error) => {
      if (isRateLimited(error)) {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.tooManyAttemptsTryLaterError')
        )
        return
      }
      if (!(isAxiosError(error) && error.response?.status === 400)) {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongTryAgainError')
        )
        return
      }

      setToastNotification(
        ToastType.DANGER,
        translate('verifyEmailView.setPassword.flashMessage.failure')
      )
      onLinkDead()
    },
  })

  return (
    <VerifyEmailLayout>
      <div className="flex max-w-[500px] flex-col">
        <p style={theme.textVariants.h2} className="mt-3">
          {translate('verifyEmailView.setPassword.title')}
        </p>
        <p style={theme.textVariants.base} className="mt-2">
          {translate('verifyEmailView.setPassword.subtitle')}
        </p>
        <div className="mt-6">
          <PasswordForm
            setPassword={(password) => mutateSetPassword(password)}
            isSettingPassword={isPending}
            passwordLabel={translate(
              'verifyEmailView.setPassword.passwordLabel'
            )}
            submitLabel={translate(
              'verifyEmailView.setPassword.submitButtonTitle'
            )}
            // A link that has expired or has already been used is replaced from
            // this same page without a token.
            sendNewLinkUrl={reverseUrl('verify-email')}
          />
        </div>
      </div>
    </VerifyEmailLayout>
  )
}

const RequestLink = ({
  linkWasDead = false,
}: {
  linkWasDead?: boolean
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { setToastNotification } = useToastNotifications()
  const { userId, userIsVerified, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const fromSignup = (location.state as { email?: string } | null)?.email

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: {
      email: `${fromSignup ?? ''}`.trim().toLowerCase(),
    },
  })

  const { mutate: mutateSendLink, isPending } = useMutation({
    mutationFn: async (email: string) => {
      await createUser({
        email: email.toLowerCase(),
        language: getLanguage(i18n.language),
      })
    },
    onSuccess: () => {
      setToastNotification(
        ToastType.SUCCESS,
        translate('verifyEmailView.flashMessage.resendVerifyEmailSuccess')
      )
    },
    onError: (error) => {
      setToastNotification(
        ToastType.DANGER,
        translate(
          isRateLimited(error)
            ? 'flashMessage.tooManyAttemptsError'
            : 'verifyEmailView.flashMessage.resendVerifyEmailFailure'
        )
      )
    },
  })

  const onSubmit = (data: InferType<typeof resetPasswordSchema>): void => {
    mutateSendLink(data.email)
  }

  const onError = (): void => {
    if (!isValid) return
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  return (
    <VerifyEmailLayout>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <div className="flex max-w-[500px] flex-col">
          <p style={theme.textVariants.h2} className="mt-3">
            {translate(
              linkWasDead
                ? 'verifyEmailView.linkExpiredTitle'
                : 'verifyEmailView.title'
            )}
          </p>
          <p
            style={theme.textVariants.base}
            className="mt-2 [overflow-wrap:anywhere]"
          >
            {translate(
              linkWasDead
                ? 'verifyEmailView.linkExpiredSubtitle'
                : 'verifyEmailView.subtitle'
            )}
          </p>
          <Controller
            control={control}
            name="email"
            render={({ field: { value, name, onChange, onBlur } }) => (
              <FormControl
                className="mt-6"
                error={errors.email && getErrorMessage(errors.email)}
                name={name}
                label={translate('verifyEmailView.form.labels.email')}
              >
                <TextInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  type="email"
                  error={!!errors.email}
                />
              </FormControl>
            )}
          />
          <Button
            type="submit"
            variant={ButtonVariant.PRIMARY}
            className="mt-3 w-full md:w-80"
            label={translate('verifyEmailView.sendAgain')}
            loading={isPending}
            disabled={isPending}
            icon={IconChoices.REFRESH}
          />
        </div>
      </form>
      <div className="mt-10 max-w-[500px] space-y-3">
        <p style={theme.textVariants.base}>
          {translate('verifyEmailView.invalidEmail')}
        </p>
        <Button
          wrapperClassName="w-full md:w-80"
          className="w-full"
          onClick={() => {
            if (userId && userIsVerified === false) {
              logout(reverseUrl('login'))
              return
            }
            navigate(reverseUrl('login'), { replace: true })
          }}
          label={translate('verifyEmailView.startOverButtonTitle')}
        />
      </div>
    </VerifyEmailLayout>
  )
}

export const VerifyEmailView = (): React.ReactNode => {
  useDocumentTitle(reverseDocumentTitle('verify-email'))
  const token = useLinkToken()
  const liveness = useLinkLiveness(token)

  const [diedOnSubmit, setDiedOnSubmit] = React.useState(false)

  if (token == null || token.length === 0) {
    return <RequestLink />
  }

  if (liveness === 'checking') {
    return (
      <VerifyEmailLayout>
        <SpinnerWrapper />
      </VerifyEmailLayout>
    )
  }

  if (liveness === 'dead' || diedOnSubmit) {
    return <RequestLink linkWasDead />
  }

  return <SetPassword token={token} onLinkDead={() => setDiedOnSubmit(true)} />
}
